<template>
    <div class="preview-overlay">
        <div class="preview-close" @click="$router.back()">
            <i class="el-icon-close"></i>
        </div>

        <!-- 加载中 -->
        <div v-if="loading" class="loading-wrap">
            <van-loading size="36px" color="#fff" />
            <span class="loading-text">加载中...</span>
        </div>

        <!-- 播放区域 - 完全复用 HomeView 的 preview-content 结构 -->
        <div class="preview-content" :style="previewStyle" @click.stop>
            <!-- 非在线：直接显示图片/视频 -->
            <template v-if="screenData && +screenData.terminalState !== 1">
                <template v-if="isVideo(currentSrc)">
                    <video :src="currentSrc" class="preview-media"
                           controls autoplay playsinline webkit-playsinline
                           @canplay="onLoaded"></video>
                </template>
                <template v-else>
                    <img :src="currentSrc" class="preview-media" @load="onLoaded" />
                </template>
            </template>
            <!-- 在线：用 MonitorScreen 播放 -->
            <template v-else-if="screenData && planData">
                <MonitorScreen
                    :screenInfo="mergedData"
                    :previewContainerW="previewContainerW"
                    :previewContainerH="previewContainerH"
                    :key="'monitor_' + screenId" />
            </template>
        </div>
    </div>
</template>

<script>
import { resourceUrl } from '@/utils/base';
import MonitorScreen from './mobile/MonitorScreen';

export default {
    name: 'SingleMonitorView',
    components: { MonitorScreen },
    data() {
        return {
            screenId: '',
            screenData: null,
            planData: null,
            loading: true,
            currentSrc: '',
            previewStyle: {},
            previewContainerW: 0,
            previewContainerH: 0,
        };
    },
    computed: {
        mergedData() {
            if (!this.screenData || !this.planData) return {};
            return { ...this.screenData, ...this.planData };
        },
    },
    created() {
        this.screenId = this.$route.query.screenId || '';
        if (!this.screenId) {
            this.$toast('缺少参数');
            this.loading = false;
            return;
        }
        this.loadData();
    },
    mounted() {
        this._resizeHandler = () => this.updatePreviewLayout();
        window.addEventListener('resize', this._resizeHandler);
        window.addEventListener('orientationchange', this._resizeHandler);
    },
    beforeDestroy() {
        document.body.style.overflow = '';
        window.removeEventListener('resize', this._resizeHandler);
        window.removeEventListener('orientationchange', this._resizeHandler);
    },
    methods: {
        isVideo(url) {
            return url && url.toLowerCase().endsWith('.mp4');
        },
        onLoaded() {
            this.loading = false;
        },
        async loadData() {
            try {
                // 读取缓存
                const cached = sessionStorage.getItem('singleMonitorCache');
                let screen = null;
                if (cached) {
                    try {
                        const d = JSON.parse(cached);
                        if (d.screenId === this.screenId) screen = d;
                    } catch (e) {}
                }
                sessionStorage.removeItem('singleMonitorCache');

                // 无缓存则请求
                if (!screen) {
                    const res = await this.$post('/monitor/queryPageListV4', {
                        pageNum: 0, pageSize: 1,
                        screenId: this.screenId,
                        superviseLevelList: [1, 2],
                    });
                    if (res.rescode === 200 && res.data?.dataList?.length) {
                        screen = res.data.dataList[0];
                        if (screen.district === '岳麓区') screen.district = '湘江新区';
                    }
                }

                if (!screen) {
                    this.$toast('未找到媒体信息');
                    this.loading = false;
                    return;
                }

                this.screenData = screen;
                this.updatePreviewLayout();

                // 非在线：直接显示实景图
                if (+screen.terminalState !== 1) {
                    this.showStatic(screen);
                    return;
                }

                // 在线：获取 plan
                await this.loadPlan(screen);
            } catch (e) {
                console.error(e);
                this.$toast('加载失败');
                this.loading = false;
            }
        },

        showStatic(screen) {
            let src = '';
            if (screen.coverThumbnailUrl) {
                src = screen.coverThumbnailUrl;
            } else if (screen.realPictureUrl) {
                const first = screen.realPictureUrl.split(',')[0];
                src = first ? resourceUrl + first : '';
            }
            if (src) {
                this.currentSrc = src;
            } else {
                this.loading = false;
            }
        },

        async loadPlan(screen) {
            try {
                const res = await this.$post('/monitor/getScreenPlanSpeedV4', {
                    forms: [{
                        screenId: screen.screenId,
                        hardwareCode: screen.hardwareCode,
                        terminalState: 1,
                    }],
                });
                if (res.rescode === 200 && res.data?.dataList?.length) {
                    const plan = res.data.dataList.find(p => p.mediaId === screen.screenId);
                    if (plan) {
                        this.planData = plan;
                        this.loading = false;
                        return;
                    }
                }
                // plan 获取失败，降级显示实景图
                this.showStatic(screen);
            } catch (e) {
                console.error(e);
                this.showStatic(screen);
            }
        },

        // 完全复用 HomeView 的 updatePreviewLayout 逻辑
        updatePreviewLayout() {
            if (!this.screenData) return;

            const resolution = this.screenData.screenResolution || '';
            let width = 1920, height = 1080;
            if (resolution) {
                const parts = resolution.split(/[*xX]/);
                if (parts.length === 2) {
                    const w = parseInt(parts[0], 10);
                    const h = parseInt(parts[1], 10);
                    if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
                        width = w;
                        height = h;
                    }
                }
            }

            const isContentLandscape = width > height;
            const isPhoneLandscape = window.innerWidth > window.innerHeight;
            const contentRatio = width / height;
            let previewW, previewH;

            // 横屏内容 + 手机竖屏 → 旋转90度
            if (isContentLandscape && !isPhoneLandscape) {
                const screenW = window.innerHeight;
                const screenH = window.innerWidth;
                const screenRatio = screenW / screenH;

                if (contentRatio > screenRatio) {
                    previewW = screenW;
                    previewH = previewW / contentRatio;
                } else {
                    previewH = screenH;
                    previewW = previewH * contentRatio;
                }

                this.previewContainerW = Math.floor(previewW);
                this.previewContainerH = Math.floor(previewH);
                this.previewStyle = {
                    width: `${this.previewContainerW}px`,
                    height: `${this.previewContainerH}px`,
                    transform: 'rotate(90deg)',
                };
            } else {
                // 横屏+横屏 或 竖屏内容
                const screenW = window.innerWidth;
                const screenH = window.innerHeight;
                const screenRatio = screenW / screenH;

                if (contentRatio > screenRatio) {
                    previewW = screenW;
                    previewH = previewW / contentRatio;
                } else {
                    previewH = screenH;
                    previewW = previewH * contentRatio;
                }

                this.previewContainerW = Math.floor(previewW);
                this.previewContainerH = Math.floor(previewH);
                this.previewStyle = {
                    width: `${this.previewContainerW}px`,
                    height: `${this.previewContainerH}px`,
                };
            }
        },
    },
};
</script>

<style scoped>
/* 完全复用 HomeView 的预览样式 */
.preview-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10000;
    transition: background 0.3s;
}

.preview-close:hover {
    background: rgba(255, 255, 255, 0.3);
}

.preview-close i {
    font-size: 24px;
    color: #fff;
}

.preview-content {
    background: #0e0e0e;
}

.preview-media {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.loading-wrap {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    z-index: 10000;
}

.loading-text {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
}
</style>
